import { Button, ButtonProps, IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { ButtonLink } from './ButtonLink';
import { IconButtonLink } from './IconButtonLink';

export interface AdaptiveButtonProps {
  readonly text: string;
  readonly icon: React.ReactNode;
  readonly display: 'short' | 'long';
  readonly to?: string;
  readonly openInNewTab?: boolean;
  readonly target?: string;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  readonly color?: ButtonProps['color'];
  readonly size?: ButtonProps['size'];
  readonly variant?: ButtonProps['variant'];
  readonly sx?: ButtonProps['sx'];
}

/**
 * Adaptive button component that can display as either an icon button or a normal button
 * based on the display prop. Can also work as a link when 'to' prop is provided.
 *
 * - display="short": Renders as icon button with tooltip showing the text
 * - display="long": Renders as normal MUI button with text
 * - When 'to' prop is provided: Uses ButtonLink or IconButtonLink components
 */
export const AdaptiveButton: React.FC<AdaptiveButtonProps> = ({
  text,
  icon,
  display,
  to,
  openInNewTab = false,
  target,
  onClick,
  disabled = false,
  color = 'primary',
  size = 'medium',
  variant = 'outlined',
  sx,
}) => {
  // If 'to' prop is provided, use link components
  if (to) {
    if (display === 'short') {
      return (
        <Tooltip title={text} placement="top">
          <IconButtonLink
            to={to}
            openInNewTab={openInNewTab}
            target={target}
            disabled={disabled}
            color={color}
            size={size}
            sx={sx}
          >
            {icon}
          </IconButtonLink>
        </Tooltip>
      );
    } else {
      return (
        <ButtonLink
          to={to}
          openInNewTab={openInNewTab}
          target={target}
          disabled={disabled}
          color={color}
          size={size}
          sx={sx}
          startIcon={icon}
          variant={variant}
        >
          {text}
        </ButtonLink>
      );
    }
  }

  // Regular button components (not links)
  if (display === 'short') {
    return (
      <Tooltip title={text} placement="top">
        <IconButton onClick={onClick} disabled={disabled} color={color} size={size} sx={sx}>
          {icon}
        </IconButton>
      </Tooltip>
    );
  } else {
    return (
      <Button
        onClick={onClick}
        disabled={disabled}
        color={color}
        size={size}
        sx={sx}
        startIcon={icon}
        variant={variant}
      >
        {text}
      </Button>
    );
  }
};
